/**
 * `/contacts/new` — add-contact (design §2.6).
 *
 * The form itself is `components/contacts/contact-form.tsx`; this route wires it
 * to `events.current` (met-at candidates), `events.list` (the escape hatch) and
 * `contacts.create`, then navigates to the new contact's detail page.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import {
  useCreateContactMutation,
  useCurrentEventsQuery,
  useEventsQuery,
} from '../../../api/queries.ts';
import { ContactForm } from '../../../components/contacts/contact-form.tsx';
import type { MetAtOption } from '../../../components/contacts/met-at-field.tsx';
import { PageBody, PageHeader } from '../../../components/page.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';

export const Route = createFileRoute('/_authed/contacts/new')({
  component: NewContactPage,
  errorComponent: RouteErrorPanel,
});

function NewContactPage() {
  const navigate = useNavigate();
  const currentEvents = useCurrentEventsQuery();
  const events = useEventsQuery();
  const create = useCreateContactMutation();

  const candidates = useMemo<MetAtOption[]>(
    () =>
      (currentEvents.data?.candidates ?? []).map((candidate) => ({
        id: candidate.event.id,
        name: candidate.event.name,
        startsAt: candidate.event.startsAt,
        reason: candidate.reason,
      })),
    [currentEvents.data],
  );

  const recent = useMemo<MetAtOption[]>(
    () =>
      (events.data?.items ?? []).map((event) => ({
        id: event.id,
        name: event.name,
        startsAt: event.startsAt,
      })),
    [events.data],
  );

  return (
    <>
      <PageHeader
        title="Add contact"
        description="First name is the only thing required. Notes are what make the record worth keeping."
      />
      <PageBody>
        <ContactForm
          metAtCandidates={candidates}
          metAtEvents={recent}
          metAtLoading={currentEvents.isLoading}
          onSubmit={(input, meta) =>
            create.mutateAsync({
              input,
              idempotencyKey: meta.idempotencyKey,
              correlationId: meta.correlationId,
            })
          }
          onCreated={(contactId) => {
            void navigate({ to: '/contacts/$contactId', params: { contactId } });
          }}
          onCancel={() => {
            void navigate({ to: '/contacts' });
          }}
        />
      </PageBody>
    </>
  );
}
